﻿import React from 'react'
import { Button, Text, Input, Heading, LoadingSpinner } from '../../components/ui'
import { PermissionGuard } from '../../components/PermissionGuard'
import { projetosService, RoadmapItem } from '../../services/projetos'
import { supabase } from '../../lib/supabase'
import { modulesService } from '../../services/modules'
import { Module } from '../../types'

const GestaoProjetosPage: React.FC = () => {
  const [loading, setLoading] = React.useState(true)
  const [items, setItems] = React.useState<RoadmapItem[]>([])
  const [groups, setGroups] = React.useState<any[]>([])
  const [labels, setLabels] = React.useState<any[]>([])
  const [activeTab, setActiveTab] = React.useState<'itens' | 'grupos' | 'labels'>('itens')
  const [error, setError] = React.useState<string | null>(null)
  const [query, setQuery] = React.useState('')
  const [isModalOpen, setIsModalOpen] = React.useState(false)
  const [editing, setEditing] = React.useState<Partial<RoadmapItem> | null>(null)
  const [stakeholderEmails, setStakeholderEmails] = React.useState<string>('')
  const [selectedId, setSelectedId] = React.useState<string | null>(null)
  const [startBr, setStartBr] = React.useState<string>('')
  const [endBr, setEndBr] = React.useState<string>('')
  const [module, setModule] = React.useState<Module | null>(null)
  const [isLabelModalOpen, setIsLabelModalOpen] = React.useState(false)
  const [editingLabel, setEditingLabel] = React.useState<{ id?: string; name: string; color: string } | null>(null)
  const [selectedLabelIds, setSelectedLabelIds] = React.useState<string[]>([])

  // Helpers de data (dd/mm/aaaa <-> ISO yyyy-mm-dd)
  const isoToBr = (iso?: string | null): string => {
    if (!iso) return ''
    const d = new Date(iso)
    if (isNaN(d.getTime())) return ''
    const dd = String(d.getDate()).padStart(2, '0')
    const mm = String(d.getMonth() + 1).padStart(2, '0')
    const yyyy = String(d.getFullYear())
    return `${dd}/${mm}/${yyyy}`
  }
  const brToIso = (br?: string | null): string | null => {
    if (!br) return null
    const m = br.match(/^(\d{2})\/(\d{2})\/(\d{4})$/)
    if (!m) return null
    const [, dd, mm, yyyy] = m
    // Monta ISO simples (sem timezone): yyyy-mm-dd
    return `${yyyy}-${mm}-${dd}`
  }
  const maskBr = (v: string): string => {
    const digits = v.replace(/\D/g, '').slice(0, 8)
    const a = digits.slice(0, 2)
    const b = digits.slice(2, 4)
    const c = digits.slice(4, 8)
    if (digits.length <= 2) return a
    if (digits.length <= 4) return `${a}/${b}`
    return `${a}/${b}/${c}`
  }

  const load = React.useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [grs, lbs, its] = await Promise.all([
        projetosService.getGroups(),
        projetosService.getLabels(),
        projetosService.searchItems({})
      ])
      setGroups(grs)
      setLabels(lbs)
      setItems(its)
    } catch (e: any) {
      console.error('Erro ao carregar Gestão de Projetos:', e)
      setError('Não foi possível carregar os dados do módulo Projetos. Tente novamente em alguns instantes.')
    } finally {
      setLoading(false)
    }
  }, [])

  React.useEffect(() => { load() }, [load])
  
  React.useEffect(() => {
    const loadModule = async () => {
      const { data } = await modulesService.getByName('projetos_gestao')
      if (data) setModule(data)
    }
    loadModule()
  }, [])

  const openNew = () => {
    setEditing({
      title: '',
      description: '',
      group_id: null,
      status: 'planejado',
      start_date: null,
      end_date: null
    })
    setStartBr('')
    setEndBr('')
    setSelectedLabelIds([])
    setStakeholderEmails('')
    setIsModalOpen(true)
  }
  const openEdit = async (it: RoadmapItem) => {
    setEditing({ ...it })
    setStartBr(isoToBr(it.start_date || null))
    setEndBr(isoToBr(it.end_date || null))
    setSelectedLabelIds(it.label_ids || [])
    
    // Carregar stakeholders do item
    if (it.id) {
      const { data: stakeholders } = await supabase
        .from('roadmap_item_stakeholders')
        .select('user_id, profiles!roadmap_item_stakeholders_user_id_fkey(email)')
        .eq('item_id', it.id)
      
      if (stakeholders && stakeholders.length > 0) {
        const emails = stakeholders
          .map((s: any) => s.profiles?.email)
          .filter(Boolean)
          .join(', ')
        setStakeholderEmails(emails)
      } else {
        setStakeholderEmails('')
      }
    } else {
      setStakeholderEmails('')
    }
    
    setIsModalOpen(true)
  }
  const save = async () => {
    if (!editing?.title) return
    let payload: any = { ...editing }
    // Converter datas BR -> ISO
    payload.start_date = brToIso(startBr)
    payload.end_date = brToIso(endBr)
    // Incluir labels selecionadas
    payload.label_ids = selectedLabelIds
    // Converter e-mails em stakeholders
    if (stakeholderEmails && stakeholderEmails.trim().length > 0) {
      const emails = stakeholderEmails.split(',').map(e => e.trim()).filter(Boolean)
      if (emails.length > 0) {
        const { data: profs } = await supabase.from('profiles').select('id,email').in('email', emails)
        const stakeholders = (profs || []).map((p: any) => ({ user_id: p.id, role: 'stakeholder' }))
        payload.stakeholders = stakeholders
      }
    }
    await projetosService.upsertItem(payload)
    setIsModalOpen(false)
    setEditing(null)
    setSelectedLabelIds([])
    setStakeholderEmails('')
    await load()
  }

  const filtered = React.useMemo(() => {
    if (!query) return items
    const q = query.toLowerCase()
    return items.filter(i =>
      i.title.toLowerCase().includes(q) ||
      (i.description || '').toLowerCase().includes(q)
    )
  }, [items, query])

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
        <main className="flex-1 flex flex-col overflow-x-hidden overflow-y-auto bg-gray-100 dark:bg-gray-900">
          <PermissionGuard 
            moduleName="projetos_gestao" 
            permissionType="show"
            fallback={
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center">
                  <Text size="xl" className="text-gray-500 dark:text-gray-400 mb-2">
                    Acesso Negado
                  </Text>
                  <Text size="sm" color="muted">
                    Você não tem permissão para acessar esta página.
                  </Text>
                </div>
              </div>
            }
          >
            <div className="h-full flex flex-col">
              {/* Header */}
              <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between">
                  <div>
                    <Heading as="h1" size="3xl" className="text-gray-900 dark:text-white">
                      {module?.display_name || 'Projetos • Gestão'}
                    </Heading>
                    <Text size="sm" color="muted" className="mt-1">
                      {module?.description || 'Cadastre e edite itens, grupos e labels'}
                    </Text>
                  </div>
                  {activeTab === 'itens' && (
                    <PermissionGuard moduleName="projetos_gestao" permissionType="insert" fallback={null}>
                      <Button variant="solid" onClick={openNew}>Novo projeto</Button>
                    </PermissionGuard>
                  )}
                </div>
              </div>

              {/* Filtros - apenas para aba Itens */}
              {activeTab === 'itens' && (
                <div className="px-6 py-4">
                  <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4">
                    <div className="mb-4">
                      <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">
                        Filtros
                      </Heading>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end">
                      <div className="md:col-span-12">
                        <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">Buscar</Text>
                        <Input
                          type="text"
                          placeholder="Buscar por título ou descrição..."
                          value={query}
                          onChange={(e: any) => setQuery(e.target.value)}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Abas */}
              <div className="px-6">
                <div className="border-b border-gray-200 dark:border-gray-700 flex gap-2">
                  <button
                    onClick={() => setActiveTab('itens')}
                    className={`px-4 py-2 -mb-px border-b-2 ${activeTab === 'itens' ? 'border-primary-600 text-primary-600' : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'}`}
                  >
                    Projetos
                  </button>
                  <button
                    onClick={() => setActiveTab('grupos')}
                    className={`px-4 py-2 -mb-px border-b-2 ${activeTab === 'grupos' ? 'border-primary-600 text-primary-600' : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'}`}
                  >
                    Grupos
                  </button>
                  <button
                    onClick={() => setActiveTab('labels')}
                    className={`px-4 py-2 -mb-px border-b-2 ${activeTab === 'labels' ? 'border-primary-600 text-primary-600' : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'}`}
                  >
                    Labels
                  </button>
                </div>
              </div>

              {/* Conteúdo Principal */}
              <div className="flex-1 p-6 pt-0">
                <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden h-full flex flex-col">
                  {loading ? (
                    <div className="flex-1 flex items-center justify-center">
                      <LoadingSpinner size="lg" className="mx-auto" />
                    </div>
                  ) : error ? (
                    <div className="flex-1 flex items-center justify-center">
                      <div className="rounded-md border border-rose-200 dark:border-rose-800 bg-rose-50 dark:bg-rose-900/20 px-4 py-3 text-sm text-rose-700 dark:text-rose-300">
                        {error}
                      </div>
                    </div>
                  ) : (
                    <div className="px-4 py-5 sm:p-6 flex-1 flex flex-col overflow-auto">
                      {/* ITENS */}
                      {activeTab === 'itens' && (
                        <div className="flex-1 flex flex-col">
                          <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white mb-4">Projetos</Heading>
                          <div className="flex-1 overflow-auto">
                            <table className="w-full divide-y divide-gray-200 dark:divide-gray-700">
                              <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0 z-10">
                                <tr>
                                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Título</th>
                                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Grupo</th>
                                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Status</th>
                                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Início</th>
                                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Fim</th>
                                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Ações</th>
                                </tr>
                              </thead>
                              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                {filtered.length === 0 ? (
                                  <tr>
                                    <td colSpan={6} className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                                      Nenhum item encontrado.
                                    </td>
                                  </tr>
                                ) : (
                                  filtered.map((it) => (
                                    <tr
                                      key={it.id}
                                      className={`hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer ${selectedId === it.id ? 'bg-primary-50 dark:bg-primary-900/20' : ''}`}
                                      onClick={() => setSelectedId(prev => prev === it.id ? null : it.id)}
                                    >
                                      <td className="px-4 py-4 text-sm text-gray-900 dark:text-white">{it.title}</td>
                                      <td className="px-4 py-4 text-sm text-gray-700 dark:text-gray-300">
                                        {groups.find(g => g.id === it.group_id)?.name || '—'}
                                      </td>
                                      <td className="px-4 py-4 text-sm text-gray-700 dark:text-gray-300">{it.status.replace('_',' ')}</td>
                                      <td className="px-4 py-4 text-sm text-gray-700 dark:text-gray-300">{isoToBr(it.start_date || null) || '—'}</td>
                                      <td className="px-4 py-4 text-sm text-gray-700 dark:text-gray-300">{isoToBr(it.end_date || null) || '—'}</td>
                                      <td className="px-4 py-4 text-right">
                                        <PermissionGuard moduleName="projetos_gestao" permissionType="update" fallback={null}>
                                          <Button variant="outline" size="sm" onClick={(e: React.MouseEvent) => { e.stopPropagation(); openEdit(it); }}>
                                            Editar
                                          </Button>
                                        </PermissionGuard>
                                      </td>
                                    </tr>
                                  ))
                                )}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}

                      {/* GRUPOS */}
                      {activeTab === 'grupos' && (
                        <div className="flex-1 flex flex-col">
                          <div className="flex items-center justify-between mb-4">
                            <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">Grupos</Heading>
                            <Button
                              variant="solid"
                              onClick={async () => {
                                const name = window.prompt('Nome do grupo:')
                                if (!name) return
                                await supabase.from('roadmap_groups').insert({ name, owner_user_id: (await supabase.auth.getUser()).data.user?.id })
                                await load()
                              }}
                            >
                              Novo grupo
                            </Button>
                          </div>
                          <div className="flex-1 overflow-auto">
                            <table className="w-full divide-y divide-gray-200 dark:divide-gray-700">
                              <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0 z-10">
                                <tr>
                                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Nome</th>
                                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Ordem</th>
                                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Ações</th>
                                </tr>
                              </thead>
                              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                {groups.map((g) => (
                                  <tr key={g.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                                    <td className="px-4 py-4 text-sm text-gray-900 dark:text-white">{g.name}</td>
                                    <td className="px-4 py-4 text-sm text-gray-700 dark:text-gray-300">{g.order_index}</td>
                                    <td className="px-4 py-4 text-right">
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={async () => {
                                          const name = window.prompt('Novo nome do grupo:', g.name) || g.name
                                          const order = Number(window.prompt('Nova ordem:', String(g.order_index)) || g.order_index)
                                          await supabase.from('roadmap_groups').update({ name, order_index: order }).eq('id', g.id)
                                          await load()
                                        }}
                                      >
                                        Editar
                                      </Button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}

                      {/* LABELS */}
                      {activeTab === 'labels' && (
                        <div className="flex-1 flex flex-col">
                          <div className="flex items-center justify-between mb-4">
                            <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">Labels</Heading>
                            <Button
                              variant="solid"
                              onClick={() => {
                                setEditingLabel({ name: '', color: '#008B95' })
                                setIsLabelModalOpen(true)
                              }}
                            >
                              Nova label
                            </Button>
                          </div>
                          <div className="flex-1 overflow-auto">
                            <table className="w-full divide-y divide-gray-200 dark:divide-gray-700">
                              <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0 z-10">
                                <tr>
                                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Nome</th>
                                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Cor</th>
                                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Ações</th>
                                </tr>
                              </thead>
                              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                {labels.map((l) => (
                                  <tr key={l.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                                    <td className="px-4 py-4 text-sm text-gray-900 dark:text-white">{l.name}</td>
                                    <td className="px-4 py-4">
                                      <div className="flex items-center gap-2">
                                        <div
                                          className="w-6 h-6 rounded border border-gray-300 dark:border-gray-600"
                                          style={{ backgroundColor: l.color || '#008B95' }}
                                        />
                                        <span className="text-sm text-gray-600 dark:text-gray-400">{l.color || '#008B95'}</span>
                                      </div>
                                    </td>
                                    <td className="px-4 py-4 text-right">
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => {
                                          setEditingLabel({ id: l.id, name: l.name, color: l.color || '#008B95' })
                                          setIsLabelModalOpen(true)
                                        }}
                                      >
                                        Editar
                                      </Button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Modal de Label */}
              {isLabelModalOpen && editingLabel && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
                  <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl w-full max-w-lg p-6">
                    <div className="flex items-center justify-between mb-4">
                      <Text size="lg" className="text-gray-900 dark:text-white">
                        {editingLabel.id ? 'Editar label' : 'Nova label'}
                      </Text>
                      <Button variant="ghost" onClick={() => { setIsLabelModalOpen(false); setEditingLabel(null) }}>Fechar</Button>
                    </div>

                    <div className="space-y-4">
                      <div>
                        <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-1">Nome</Text>
                        <Input
                          value={editingLabel.name}
                          onChange={(e: any) => setEditingLabel(prev => ({ ...prev!, name: e.target.value }))}
                          placeholder="Nome da label"
                        />
                      </div>
                      <div>
                        <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-1">Cor</Text>
                        <div className="flex items-center gap-3">
                          <input
                            type="color"
                            value={editingLabel.color}
                            onChange={(e) => setEditingLabel(prev => ({ ...prev!, color: e.target.value }))}
                            className="w-12 h-10 border border-gray-300 dark:border-gray-600 rounded-md cursor-pointer"
                          />
                          <Input
                            value={editingLabel.color}
                            onChange={(e: any) => setEditingLabel(prev => ({ ...prev!, color: e.target.value }))}
                            placeholder="#008B95"
                            className="flex-1"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="mt-6 flex justify-end gap-2">
                      <Button variant="ghost" onClick={() => { setIsLabelModalOpen(false); setEditingLabel(null) }}>Cancelar</Button>
                      <Button
                        variant="solid"
                        onClick={async () => {
                          if (!editingLabel.name) return
                          const { data: { user } } = await supabase.auth.getUser()
                          if (editingLabel.id) {
                            await supabase.from('roadmap_labels').update({ name: editingLabel.name, color: editingLabel.color }).eq('id', editingLabel.id)
                          } else {
                            await supabase.from('roadmap_labels').insert({ name: editingLabel.name, color: editingLabel.color, owner_user_id: user?.id })
                          }
                          setIsLabelModalOpen(false)
                          setEditingLabel(null)
                          await load()
                        }}
                        disabled={!editingLabel.name}
                      >
                        Salvar
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* Modal simples */}
              {isModalOpen && editing && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
                  <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col">
                    <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
                      <Text size="lg" className="text-gray-900 dark:text-white">
                        {editing?.id ? 'Editar projeto' : 'Novo projeto'}
                      </Text>
                      <Button variant="ghost" onClick={() => { setIsModalOpen(false); setEditing(null); setSelectedLabelIds([]); setStakeholderEmails('') }}>Fechar</Button>
                    </div>

                    <div className="flex-1 overflow-y-auto p-6">
                      <div className="space-y-4">
                      <div>
                        <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-1">Título</Text>
                        <Input
                          value={editing.title || ''}
                          onChange={(e: any) => setEditing(prev => ({ ...prev!, title: e.target.value }))}
                          placeholder="Título do item"
                        />
                      </div>
                      <div>
                        <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-1">Descrição</Text>
                        <textarea
                          className="w-full rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-gray-100"
                          rows={3}
                          value={editing.description || ''}
                          onChange={(e) => setEditing(prev => ({ ...prev!, description: e.target.value }))}
                          placeholder="Descrição resumida"
                        />
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-1">Grupo</Text>
                          <select
                            className="w-full rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-gray-100"
                            value={editing.group_id || ''}
                            onChange={(e) => setEditing(prev => ({ ...prev!, group_id: e.target.value || null }))}
                          >
                            <option value="">—</option>
                            {groups.map((g) => (
                              <option key={g.id} value={g.id}>{g.name}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-1">Status</Text>
                          <select
                            className="w-full rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-gray-100"
                            value={editing.status || 'planejado'}
                            onChange={(e) => setEditing(prev => ({ ...prev!, status: e.target.value as any }))}
                          >
                            <option value="ideia">Ideia</option>
                            <option value="planejado">Planejado</option>
                            <option value="em_andamento">Em andamento</option>
                            <option value="bloqueado">Bloqueado</option>
                            <option value="concluido">Concluído</option>
                          </select>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-1">Início (dd/mm/aaaa)</Text>
                          <Input
                            value={startBr}
                            onChange={(e: any) => setStartBr(maskBr(e.target.value))}
                            placeholder="dd/mm/aaaa"
                          />
                        </div>
                        <div>
                          <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-1">Fim (dd/mm/aaaa)</Text>
                          <Input
                            value={endBr}
                            onChange={(e: any) => setEndBr(maskBr(e.target.value))}
                            placeholder="dd/mm/aaaa"
                          />
                        </div>
                      </div>
                      <div>
                        <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">Tags</Text>
                        <div className="border border-gray-300 dark:border-gray-700 rounded-md p-3 max-h-48 overflow-y-auto bg-white dark:bg-gray-800">
                          {labels.length === 0 ? (
                            <Text size="sm" className="text-gray-500 dark:text-gray-400">Nenhuma tag cadastrada</Text>
                          ) : (
                            <div className="space-y-2">
                              {labels.map((label) => (
                                <label
                                  key={label.id}
                                  className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 p-2 rounded"
                                >
                                  <input
                                    type="checkbox"
                                    checked={selectedLabelIds.includes(label.id)}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        setSelectedLabelIds([...selectedLabelIds, label.id])
                                      } else {
                                        setSelectedLabelIds(selectedLabelIds.filter(id => id !== label.id))
                                      }
                                    }}
                                    className="rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500 dark:bg-gray-700 dark:checked:bg-primary-600"
                                  />
                                  <div className="flex items-center gap-2 flex-1">
                                    <div
                                      className="w-4 h-4 rounded border border-gray-300 dark:border-gray-600"
                                      style={{ backgroundColor: label.color || '#008B95' }}
                                    />
                                    <Text size="sm" className="text-gray-900 dark:text-white">{label.name}</Text>
                                  </div>
                                </label>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                      <div>
                        <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-1">Stakeholders (e-mails, separados por vírgula)</Text>
                        <Input
                          value={stakeholderEmails}
                          onChange={(e: any) => setStakeholderEmails(e.target.value)}
                          placeholder="ex.: user1@empresa.com, user2@empresa.com"
                        />
                      </div>
                      </div>
                    </div>

                    <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2">
                      <Button variant="ghost" onClick={() => { setIsModalOpen(false); setEditing(null); setSelectedLabelIds([]); setStakeholderEmails('') }}>Cancelar</Button>
                      <Button variant="solid" onClick={save} disabled={!editing?.title}>Salvar</Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </PermissionGuard>
        </main>
      </div>
  )
}

export default GestaoProjetosPage
